// ── Operator-published TURN config (NIP-78) ───────────────────────
//
// BCH Rooms need a TURN relay for anyone whose network blocks direct
// peer-to-peer connections (symmetric NAT, CGNAT — most mobile carriers).
// The relay credentials could simply be hard-coded in the bundle, but
// distributing them over Nostr instead means they can be **rotated
// without a redeploy**: a static credential lives in every visitor's
// browser, so being able to swap it quickly matters.
//
// SECURITY: the query is filtered to `OPERATOR_PUBKEY`. Anyone can publish
// kind 30078 with this `d` tag, and substituting a hostile relay would
// route every participant's audio through the attacker. The author filter
// is the entire trust boundary here, so it is not optional.

import { useEffect } from 'react';
import { useNostrQuery } from './useNostrQuery';
import { OPERATOR_PUBKEY } from '../lib/platform';
import {
  TURN_CONFIG_D_TAG, parseSharedTurnConfig, cacheSharedTurnConfig,
} from '../lib/rooms';

const APP_DATA_KIND = 30078;

/**
 * Subscribe to the operator's TURN announcement and cache it locally.
 *
 * Mount once, high in the tree. Returns nothing: consumers read the value
 * through `getIceServers()` / `hasTurn()`, which fall back to the cache so
 * a room opened before this resolves still gets a relay.
 */
export function useSharedTurnConfig() {
  const { events } = useNostrQuery({
    // `authors` is the trust boundary — see the note above.
    filters: [{
      kinds: [APP_DATA_KIND],
      authors: [OPERATOR_PUBKEY],
      '#d': [TURN_CONFIG_D_TAG],
      limit: 1,
    }],
  });

  useEffect(() => {
    if (events.length === 0) return;

    // Defensive: useNostrQuery already dedupes addressable events to the
    // newest per author+kind+d, but re-check the author before trusting
    // anything that ends up in the ICE config.
    const newest = events
      .filter(ev => ev.pubkey === OPERATOR_PUBKEY)
      .sort((a, b) => b.created_at - a.created_at)[0];
    if (!newest) return;

    const parsed = parseSharedTurnConfig(newest.content);
    // A published-then-emptied event clears the cache, so the operator can
    // turn the relay off for everyone by publishing `{}`.
    cacheSharedTurnConfig(parsed);
  }, [events]);
}
